import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, X, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { api, BACKEND_URL } from "../lib/api";
import { shareCtaToast } from "../lib/share";
import { useAuth } from "../lib/auth";
import UserAvatar from "./UserAvatar";

// Hard cap that matches the backend (`MAX_BYTES` in routes/photos.py).
// Real-world uploads are compressed to ~1.5 MB below — the cap only guards
// against people bypassing the browser layer.
const MAX_BYTES = 35 * 1024 * 1024;

// Compression profile — long-edge 1920 px is more than enough for the
// full-screen lightbox on a 2× phone (max ≈ 828 pt wide) and keeps the
// median photo under 300 KB on jpeg, well below the 1.5 MB target.
const COMPRESSION_OPTS = {
  maxSizeMB: 1.5,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.82,
};

/**
 * Community photo gallery.
 *
 * Props:
 *   locationId  - trail/campsite id (posts to /locations/{id}/photos)
 *   basecampId  - basecamp id       (posts to /basecamps/{id}/photos)
 *   name        - display name of the spot (used in the post-upload share CTA)
 * Exactly one of locationId/basecampId must be supplied.
 */
export default function PhotoGallery({ locationId, basecampId, name }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  // Which REST resource to hit — trails/campsites vs basecamps. Kept in one
  // place so the fetch, upload and delete calls all agree.
  const resourcePath = basecampId ? `basecamps/${basecampId}` : `locations/${locationId}`;
  const resourceKey = basecampId ? `bc:${basecampId}` : `loc:${locationId}`;

  const photos = useQuery({
    queryKey: ["photos", resourceKey],
    queryFn: async () => (await api.get(`/${resourcePath}/photos`)).data.items,
  });

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!user) {
      toast.error("Sign in to share photos.");
      return;
    }

    // Enforce raw size cap early so a mis-tap on a 40 MB RAW file fails
    // immediately, before we start the (potentially slow) worker.
    const oversized = files.find((f) => f.size > MAX_BYTES);
    if (oversized) {
      toast.error(`"${oversized.name}" is over 35 MB — please pick a smaller image.`);
      if (e.target) e.target.value = "";
      return;
    }

    setCompressing(true);
    let compressed;
    try {
      compressed = await Promise.all(
        files.slice(0, 5).map(async (f) => {
          try {
            const out = await imageCompression(f, COMPRESSION_OPTS);
            return new File([out], f.name, { type: out.type || f.type });
          } catch {
            // Fall back to the raw file if compression fails (e.g. huge PNG
            // hits worker OOM) — the 35 MB cap still guards the server.
            return f;
          }
        }),
      );
    } finally {
      setCompressing(false);
    }

    setUploading(true);
    let ok = 0;
    for (const file of compressed) {
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post(`/${resourcePath}/photos`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        const msg = err?.response?.data?.detail || "Upload failed";
        toast.error(msg);
      }
    }
    setUploading(false);
    if (ok) {
      // Post-upload share CTA — reuses the exact shareLink flow the page-level
      // share buttons use, pointing at the OG landing URL for this resource.
      const kind = basecampId ? "basecamp" : "trail";
      shareCtaToast({
        url: `${window.location.origin}/api/og/${basecampId ? `basecamp/${basecampId}` : `location/${locationId}`}`,
        title: `Check out this ${kind} on TrailDog!`,
        text: `I just found this great dog-friendly ${kind} on TrailDog: ${name || "a great spot"}. Check it out here!`,
        event: basecampId ? "share_basecamp" : "share_trail",
        props: {
          ...(basecampId ? { basecamp_id: basecampId } : { trail_id: locationId }),
          source: "post_photo_upload",
        },
        description: `${ok} photo${ok > 1 ? "s" : ""} added`,
      });
    }
    if (e.target) e.target.value = "";
    qc.invalidateQueries({ queryKey: ["photos", resourceKey] });
  };

  const remove = async (photo_id) => {
    try {
      await api.delete(`/${resourcePath}/photos/${photo_id}`);
      toast.success("Photo removed");
      qc.invalidateQueries({ queryKey: ["photos", resourceKey] });
      setLightbox(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  const items = photos.data || [];
  const busy = uploading || compressing;
  const btnLabel = compressing
    ? "Compressing…"
    : uploading
      ? "Uploading…"
      : items.length ? "Add photo" : "Share a photo";

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="photo-gallery">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="td-section-label">Walker photos</div>
          <p className="text-sm text-brand-subtle mt-1">
            {items.length === 0
              ? "No walker photos yet. Be the first to share what it really looks like."
              : `${items.length} community photo${items.length === 1 ? "" : "s"} from real walkers.`}
          </p>
        </div>
        <button
          data-testid="upload-photo-btn"
          onClick={onPick}
          disabled={busy}
          className="td-btn-secondary !py-2 !px-4 !text-sm shrink-0 disabled:opacity-70"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
          {btnLabel}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={onChange}
          data-testid="photo-file-input"
        />
      </div>

      {items.length > 0 && (
        <div
          className="flex gap-2 mt-4 overflow-x-auto -mx-1 px-1 pb-2 snap-x snap-mandatory"
          data-testid="photo-carousel"
        >
          {items.map((p) => (
            <button
              key={p.photo_id}
              type="button"
              onClick={() => setLightbox(p)}
              className="shrink-0 w-28 sm:w-32 aspect-square rounded-2xl overflow-hidden border border-brand-border bg-brand-sand relative group snap-start"
              data-testid={`photo-${p.photo_id}`}
            >
              <img
                src={`${BACKEND_URL}${p.url}`}
                alt={`Shared by ${p.user_name}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {!user && (
        <p className="mt-3 text-xs text-brand-subtle">
          Sign in to share your own walker photos with the community.
        </p>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[1100] bg-brand-text/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-brand-surface flex items-center justify-center"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X size={20} className="text-brand-text" />
          </button>
          <div className="max-w-3xl w-full max-h-[80vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${BACKEND_URL}${lightbox.url}`}
              alt={`Shared by ${lightbox.user_name}`}
              className="max-w-full max-h-[70vh] rounded-3xl border border-brand-border/40 shadow-2xl object-contain bg-brand-text/40"
            />
            <div className="mt-3 flex items-center justify-between w-full text-brand-sand">
              <div className="text-sm flex items-center gap-2">
                <UserAvatar
                  user={{
                    name: lightbox.user_name,
                    avatar_url: lightbox.user_avatar_url,
                    avatar_updated_at: lightbox.user_avatar_updated_at,
                    picture: lightbox.user_picture,
                  }}
                  size={32}
                  testId={`photo-credit-avatar-${lightbox.photo_id}`}
                />
                Shared by <span className="font-medium">{lightbox.user_name}</span>
              </div>
              {user && (
                <button
                  data-testid={`remove-photo-${lightbox.photo_id}`}
                  onClick={() => remove(lightbox.photo_id)}
                  className="text-sm inline-flex items-center gap-1.5 text-brand-sand/80 hover:text-brand-error"
                >
                  <Trash2 size={14} /> Remove (if yours)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
